import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { StaffSessionService } from './staff/services/staff-session.service';
import { staffMenuBarFeature } from './staff/constant/staff-menu-bar.feature';
import { STAFF_DEFAULT_SCREEN } from './staff/constant/staff-default-screen.config';

function getCookieValue(name: string): string | null {
  const cookie = document.cookie.split(';').find(c => c.trim().startsWith(name + '='));
  return cookie ? cookie.split('=')[1] : null;
}

export const authGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const tokenValue = getCookieValue('usertoken');
  if (!tokenValue) {
    router.navigate(["/login"]);
    return false;
  }
  return true;
};

export const adminGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const tokenValue = getCookieValue('usertoken');
  if (!tokenValue) {
    router.navigate(["/login"]);
    return false;
  }
  if (getCookieValue('loginMode') === 'STAFF') {
    router.navigate(["/staff", STAFF_DEFAULT_SCREEN]);
    return false;
  }
  return true;
};

export const staffGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const tokenValue = getCookieValue('usertoken');
  if (!tokenValue) {
    router.navigate(["/login"]);
    return false;
  }
  if (getCookieValue('loginMode') !== 'STAFF') {
    router.navigate(["/pagenotfound"]);
    return false;
  }
  return true;
};

// Blocks direct navigation to a class-tool route (diary/attendance/timetable/
// activity/chat) when the school has disabled that feature — the hard gate
// from mapFeatures() applies here too, not just to whether the tile renders
// on Class Overview. Redirects back to the class overview page rather than
// pagenotfound, since the class itself is a valid destination.
export function featureGuard(featureName: string): CanActivateFn {
  return (route) => {
    const router = inject(Router);
    const staffSession = inject(StaffSessionService);
    if (!staffSession.isFeatureEnabled(featureName)) {
      const classId = route.paramMap.get('id');
      router.navigate(classId ? ['/staff/class', classId] : ['/staff/classes']);
      return false;
    }
    return true;
  };
}

// Blocks direct navigation to a route whose sidebar/bottom-nav item is
// turned off in staff-menu-bar.feature.ts — hiding the nav link isn't
// enough on its own, since the route itself stayed reachable by URL.
// Unlike featureGuard, these routes have no class context to fall back to,
// so they land on the staff 404 page instead.
export function menuFeatureGuard(menuKey: keyof typeof staffMenuBarFeature): CanActivateFn {
  return () => {
    const router = inject(Router);
    if (!staffMenuBarFeature[menuKey]) {
      router.navigate(['/staff', '404']);
      return false;
    }
    return true;
  };
}

// Same purpose as menuFeatureGuard, but for nav items whose visibility also
// depends on real backend state (school feature flags / extension services /
// staff role), not just the static build-time menu config — see
// StaffLayoutComponent's showBroadcast/showFeeCollection/etc. getters,
// which this mirrors so a hidden nav item's route can't be reached directly
// by URL either.
export function dynamicMenuFeatureGuard(check: (staffSession: StaffSessionService) => boolean): CanActivateFn {
  return () => {
    const router = inject(Router);
    const staffSession = inject(StaffSessionService);
    if (!check(staffSession)) {
      router.navigate(['/staff', '404']);
      return false;
    }
    return true;
  };
}

function hasFeeAdminRole(staffSession: StaffSessionService): boolean {
  const info = staffSession.getSchoolAndStaffInfo();
  const staffDetails = staffSession.getStaffDetails();
  const isFinanceAdmin = (info?.isFinanceAdmin ?? staffDetails?.isFinanceAdmin) === 1;
  const isFeeDefaulterAdmin = (info?.isFeeDefaulterAdmin ?? staffDetails?.isFeeDefaulterAdmin) === 1;
  return isFinanceAdmin || isFeeDefaulterAdmin;
}

function hasSuperAdminRole(staffSession: StaffSessionService): boolean {
  const info = staffSession.getSchoolAndStaffInfo();
  const staffDetails = staffSession.getStaffDetails();
  return !!(info?.isSuperAdmin ?? staffDetails?.isSuperAdmin);
}

export const broadcastMenuGuard = dynamicMenuFeatureGuard(s => s.isFeatureEnabled('BROADCAST_VIEW'));
export const profileMenuGuard = dynamicMenuFeatureGuard(s => s.isFeatureEnabled('USER_PROFILE_VIEW'));
export const searchMenuGuard = dynamicMenuFeatureGuard(s => s.isFeatureEnabled('SEARCH_PROFILE'));
export const vehicleManagementMenuGuard = dynamicMenuFeatureGuard(s => s.isFeatureEnabled('VEHICLE_MANAGEMENT'));
export const academicCalendarMenuGuard = dynamicMenuFeatureGuard(s => s.isExtensionServiceEnabled('ACADEMIC_CALENDAR'));
export const studentCheckInMenuGuard = dynamicMenuFeatureGuard(s => s.isExtensionServiceEnabled('STUDENT_CHECK_IN_CHECK_OUT'));
export const feeCollectionMenuGuard = dynamicMenuFeatureGuard(s =>
  s.isFeatureEnabled('SCHOOL_ADMIN_FEE_COLLECTION_VIEW') && hasFeeAdminRole(s)
);
export const viewStaffAttendanceMenuGuard = dynamicMenuFeatureGuard(s =>
  s.isExtensionServiceEnabled('HOURHIVE') && hasSuperAdminRole(s)
);
export const leaveManagementMenuGuard = dynamicMenuFeatureGuard(s => s.isFeatureExplicitlyEnabled('STUDENT_LEAVE_MANAGEMENT'));
